/**
 * Catalog admin shared bits — collection metadata, default form values,
 * list-row renderers. Used by AdminCatalog.jsx.
 *
 * Renderers are defensive (`?.` everywhere) — never throw on a row that's
 * missing a field, since rows can briefly carry stale data during tab
 * switches before the new fetch settles.
 */
export const SLUGS = ["medications", "ingredients", "interactions", "products"];

function _label(map, id, fallbackLen = 8) {
  if (!id) return "—";
  const found = (map || []).find((o) => o.id === id);
  if (found) {
    const base = found.generic_name || found.name || id.slice(0, fallbackLen);
    const brand = (found.brand_names || [])[0];
    return brand && brand.toLowerCase() !== base.toLowerCase() ? `${base} (${brand})` : base;
  }
  return id.slice(0, fallbackLen);
}

export const CATALOG_META = {
  medications: {
    label: "Medications",
    primary: (r) => r?.generic_name || "—",
    secondary: (r) =>
      [(r?.brand_names || []).join(", "), r?.category]
        .filter(Boolean).join(" · "),
    searchPlaceholder: "Search by generic name, brand, or category…",
  },
  ingredients: {
    label: "Ingredients",
    primary: (r) => r?.name || "—",
    secondary: (r) =>
      [r?.category, (r?.aliases || []).join(", ")]
        .filter(Boolean).join(" · "),
    searchPlaceholder: "Search by name, alias, or category…",
  },
  interactions: {
    label: "Interactions",
    // Show real medication + ingredient names when pickers are available;
    // fall back to UUID slices when row is stale or pickers haven't loaded.
    primary: (r, pickers) => {
      if (!r) return "—";
      const med = _label(pickers?.medications, r.medication_id);
      const ing = _label(pickers?.ingredients, r.ingredient_id);
      return `${med}  ↔  ${ing}`;
    },
    secondary: (r) => {
      if (!r) return "—";
      const parts = [r.risk_level, r.interaction_type, r.confidence_level]
        .filter(Boolean);
      parts.push(r.show_to_users ? "🟢 public" : "🔒 internal");
      return parts.join(" · ");
    },
    searchPlaceholder: "Search by medication_id, ingredient_id, or recommendation…",
  },
  products: {
    label: "Products",
    primary: (r) => r?.full_name || `${r?.brand || ""} ${r?.name || ""}`.trim() || "—",
    secondary: (r) =>
      [r?.category, r?.barcode_upc, r?.is_safe_alternative ? "✓ safe alt" : null]
        .filter(Boolean).join(" · "),
    searchPlaceholder: "Search by name, brand, category, or barcode…",
  },
};

export const RISK_LEVELS = ["HIGH", "MODERATE", "LOW"];
export const INTERACTION_TYPES = [
  "CONTRAINDICATED", "USE_WITH_CAUTION",
  "TIMING_DEPENDENT", "DOSE_DEPENDENT",
];
export const CONFIDENCE_LEVELS = [
  "FDA_DOCUMENTED", "PEER_REVIEWED", "TEXTBOOK",
  "EXPERT_CONSENSUS", "THEORETICAL",
];

const today = () => new Date().toISOString().slice(0, 10);

export const DEFAULTS = {
  medications: () => ({
    id: "", generic_name: "", brand_names: [], category: "",
    description: "", fda_label_url: "", typical_duration: "",
    us_prevalence: "", has_course: false, active: true,
  }),
  ingredients: () => ({
    id: "", name: "", inci_name: "", aliases: [], category: "",
    description: "", commonly_found_in: "", active: true,
  }),
  interactions: () => ({
    id: "", medication_id: "", ingredient_id: "",
    risk_level: "MODERATE", interaction_type: "USE_WITH_CAUTION",
    what_happens_scientific: "", what_happens_plain: "", recommendation: "",
    source_primary: "", source_primary_url: "",
    confidence_level: "PEER_REVIEWED",
    last_reviewed: today(),
    verified: false, show_to_users: false, active: true,
  }),
  products: () => ({
    id: "", name: "", brand: "", category: "Moisturizer",
    key_ingredient_ids: [], all_ingredients_raw: "",
    barcode_upc: "", barcode_ean: "",
    is_safe_alternative: false, source: "MANUAL",
    verified: false, active: true,
  }),
};
